// Import polyfills FIRST before any other code
import "./lib/polyfills";
// Register the PWA install-event listeners as early as possible — the
// `beforeinstallprompt` event often fires before any React component mounts,
// so capturing it at module load (not per-component) is what makes the
// device-aware install button on /install reliable.
import "./lib/pwaInstall";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import { initializeSentry, setupGlobalErrorHandlers } from "./lib/sentry";
import { installDomDesyncGuard } from "./lib/domDesyncGuard";
import { safeInit } from "./lib/safeInit";
import { adoptPrerenderSnapshot } from "./lib/prerenderSnapshot";

// Telemetry init runs before createRoot().render() — every call is
// wrapped in safeInit so no initializer can ever block the app from mounting
// (#3558). Sentry stays first so later failures are reported.
safeInit("sentry", initializeSentry);
safeInit("sentry-global-handlers", setupGlobalErrorHandlers);
safeInit("dom-desync-guard", installDomDesyncGuard);

// Auto-reload on stale chunk errors after deployment.
// Vite fires this event when a preloaded/dynamic-imported chunk 404s.
window.addEventListener("vite:preloadError", () => {
  const reloadedAt = sessionStorage.getItem("chunk-reload");
  const now = Date.now();
  // Guard against infinite reload loops — only reload once per 10 seconds
  if (!reloadedAt || now - Number(reloadedAt) > 10_000) {
    sessionStorage.setItem("chunk-reload", String(now));
    window.location.reload();
  }
});

// Wrap App with Sentry profiler
const AppWithProfiler = Sentry.withProfiler(App);

const rootElement = document.getElementById("root")!;
adoptPrerenderSnapshot(rootElement);
createRoot(rootElement).render(<AppWithProfiler />);
